import { AfterViewChecked, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { INewRequestAndOperation, NewRequestService } from "@edp-pages/requests-and-operations/services/new-request.service";
import { Step, StepsService } from "@edp-pages/requests-and-operations/services/steps.service";
import { NewModalService } from "@edp-pages/requests-and-operations/services/new-modal.service";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { getSelectedServiceAndMotive, getTimeSlotsError, loadedSlots } from "@edp-store/selectors/requests-and-operations.selector";
import { EdpToasterService } from "@edp-components/edp-toaster/edp-toaster.service";
import { TranslateService } from "@ngx-translate/core";
import { EdpToastType } from "@edp-components/edp-toaster/edp-toast-type";
import { ITimeSlot } from "@edp-models/requests-and-operations";
import { AvailableTimeSlotsAPIError } from "@edp-store/actions/requests-and-operations.actions";
import { SCREEN_SIZES } from "@edp-enums/screen-sizes.enum";
import { DeviceDetectorService } from "ngx-device-detector";
import { OperationHistoryService } from "@edp-services/operation-history.service";
import { Contract } from "@edp-components/edp-contract";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { NifService } from "@edp-services/nif.service";
import { EdpRecaptchaService } from "@edp-services/edpRecaptchaService.service";
import { RecaptchaComponent } from "ng-recaptcha";
import { EdpPaymentAgreementService } from "@edp-services/edp-payment-agreement.service";
import { SelectedComponentEnum } from "@edp-models/edp-payment-agreement.model";
import { AgreementContactResponse } from "@edp-models/edp-payment.model";
import { getUser } from "@edp-store/selectors/user.selectors";
import { EdpUser } from "@edp-models/user.model";
import { MaskEmail } from "@edp-utils/mask-email.util";

const assistanceRequestExternalCategory = "02030100";
const assistanceRequestPaymentAgreementExternalCategory = "02070800";

@Component({
    selector: "create-request-modal",
    templateUrl: "./create-request-modal.component.html",
    styleUrls: ["./create-request-modal.component.scss"],
})
export class CreateRequestModalComponent implements OnInit, OnDestroy, AfterViewChecked {
    @ViewChild("captchaRef", { static: true }) captchaRef: RecaptchaComponent;

    public advanceButtonDisabled$: BehaviorSubject<boolean>;
    public stepNum$: BehaviorSubject<number>;
    public slotsLoaded$ = false;
    public selectedSlot: ITimeSlot;
    public selfcare = false;
    public mobileSize: boolean;
    public iOSBrowser: boolean;
    public isPaymentAgreementRequest: boolean = false;

    private unsubscribe$ = new Subject();
    private contract: Contract;

    public recaptchaToken = null;
    public recaptcha: any;
    public isPage$: Observable<boolean>;
    public loadingStepTwo: boolean = true;
    private user: EdpUser;
    private isNoAgreement: boolean;

    constructor(
        private readonly stepService: StepsService,
        private readonly newRequestService: NewRequestService,
        private readonly newModalService: NewModalService,
        private readonly store: Store<IAppState>,
        private readonly toaster: EdpToasterService,
        private readonly translate: TranslateService,
        private readonly deviceDetector: DeviceDetectorService,
        private readonly cdRef: ChangeDetectorRef,
        private operationHistoryService: OperationHistoryService,
        private router: Router,
        private nifService: NifService,
        private edpRecaptchaService: EdpRecaptchaService,
        private edpPaymentAgreementService: EdpPaymentAgreementService
    ) {
        this.advanceButtonDisabled$ = this.stepService.getAdvanceButtonAvailabilityBS();
        this.stepNum$ = this.stepService.getCurrentStep();
        this.stepService.setAdvanceStepButtonAvailability(false);
        this.newRequestService
            .getRequestAndService()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((newRequest: INewRequestAndOperation) => {
                const isFromRedirect = this.router.url.includes("services"); // when user comes directly from Funciona page (check-ups)
                if (isFromRedirect) {
                    this.loadingStepTwo = false;
                }
                this.selfcare = newRequest ? ["edp.pt", "edponline"].includes(newRequest.selectedMotive.selfcare) : false;
            });
        /* this.store.pipe(select(getSelectedServiceAndMotive)).subscribe((newRequest: INewRequestAndOperation) => {
            this.selfcare = newRequest ? ["edp.pt", "edponline"].includes(newRequest.selectedMotive.selfcare) : false;
        }); */
        this.iOSBrowser = this.deviceDetector.getDeviceInfo().os.toLowerCase() === "ios";
        this.store.select(getUser).subscribe(user => {
            this.user = user;
        });

        this.edpPaymentAgreementService.getSelectedComponent().subscribe(val => {
            this.isNoAgreement = val === SelectedComponentEnum.NO_AGREEMENT;
        });

        this.isPage$ = this.edpPaymentAgreementService.getHeight();
    }

    @HostListener("window:resize")
    public onResize() {
        this.mobileSize = false;
        if (window.innerWidth < SCREEN_SIZES.WDGT) {
            this.mobileSize = true;
        }
    }

    ngOnInit(): void {
        this.stepNum$.subscribe(step => {
            if (step === 2) {
                this.loadingStepTwo = false;
            }
        });
        this.getContractId();
        this.store.pipe(select(loadedSlots)).subscribe(loaded => {
            this.slotsLoaded$ = loaded;
        });
        this.store
            .pipe(
                select(getTimeSlotsError),
                takeUntil(this.unsubscribe$),
                filter(error => !!error)
            )
            .subscribe(() => {
                this.store.dispatch(new AvailableTimeSlotsAPIError(null));
                this.toaster
                    .create(this.translate.instant("pt.edp.edponline.requests-and-operations.timeslot.error"), EdpToastType.error)
                    .show();
                this.closeModal();
            });
        this.onResize();
    }

    ngAfterViewChecked() {
        this.cdRef.detectChanges();
    }

    public backStep() {
        this.stepService.removeStep();
        this.stepService.backStep();
    }

    public closeModal() {
        this.newModalService.closeModal();
    }

    public dismissModal() {
        this.newModalService.dismissModal();
    }

    public moveStep() {
        if (this.stepNum$.getValue() === Step.timeSlot) {
            this.newModalService.submitTimeSlot(this.selectedSlot);
        } else {
            this.stepService.setCurrentStep();
            this.redirect();
        }
        this.ngAfterViewChecked();
    }

    private redirect() {
        console.log("redirect");
        const email = MaskEmail.mask(this.user?.email);
        this.newRequestService
            .getRequestAndService()
            .pipe(takeUntil(this.unsubscribe$))
            ?.subscribe((newRequest: INewRequestAndOperation) => {
                const externalCategory = newRequest?.selectedMotive.externalCategory;
                if (externalCategory === assistanceRequestExternalCategory) {
                    this.sendOperationHistoryService("CLICKED_CTA_SUPPORT_REQUEST_FUNCIONA");
                    this.newModalService.closeModal();
                    this.router.navigate(["/pedido-assistencia/pre"]);
                    return;
                }
                if (externalCategory === assistanceRequestPaymentAgreementExternalCategory) {
                    this.edpPaymentAgreementService.setSelectedComponent(SelectedComponentEnum.ELIGIBILITY);
                    this.edpPaymentAgreementService.setBottomBar(true);
                    this.edpPaymentAgreementService.setContactResponse({ contact: { email: email } });
                    this.edpPaymentAgreementService.getSelectedComponent().subscribe(val => {
                        if (val === SelectedComponentEnum.WAITING_VERIFICATION) {
                            this.edpPaymentAgreementService.setHeight(true);
                        }
                    });
                    this.executeRecaptcha();
                } else {
                    this.loadingStepTwo = false;
                }
            });
    }

    private getContractId() {
        this.store.pipe(takeUntil(this.unsubscribe$), select(getContract)).subscribe(contract => {
            if (!!contract) {
                this.contract = contract;
            }
        });
    }

    private sendOperationHistoryService(key: string) {
        this.operationHistoryService.registerWithContract(this.contract.ContractId, key, null, null, null, false);
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
        this.stepService.resetStep();
    }

    public executeRecaptcha(): void {
        if (this.captchaRef) {
            this.captchaRef.execute();
            this.recaptcha = this.captchaRef;
        } else {
            console.error("Captcha reference is not available yet.");
        }
    }

    public resolved(captchaResponse: string): void {
        this.recaptchaToken = captchaResponse;
        if (this.recaptchaToken) {
            this.edpRecaptchaService.setContactResponse(this.recaptchaToken);
            this.nifService.setNifValue(this.contract.OwnerNif);
            this.isPaymentAgreementRequest = true;
            this.loadingStepTwo = false;
        }
    }
}
